/**
 * Lettura a richiesta con stato (dati, caricamento, errore) e scarto delle risposte superate da una
 * piu' nuova. `key` cambia quando cambiano i parametri; `refreshKey` (sync_state.updated_at) rilegge
 * quando il motore scrive. Mai eccezioni verso il componente.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export function useCommerceQuery<T>(fn: () => Promise<{ data: T; error: null } | { data: null; error: string }>, key: string, enabled = true, refreshKey = '') {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const load = useCallback(async () => {
    if (!enabled) return;
    const mine = ++seq.current;
    try {
      const r = await fnRef.current();
      if (mine !== seq.current) return;
      if (r.error !== null) setError(r.error);
      else {
        setError(null);
        setData(r.data);
      }
    } catch (e) {
      if (mine === seq.current) setError(e instanceof Error ? e.message : 'errore di lettura');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    setLoading(enabled);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  useEffect(() => {
    if (refreshKey) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  return { data, loading, error, reload: load };
}
